import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { PackageService } from '../../services/package.service';
import { Observable } from 'rxjs';
import { PackageGridCardComponent } from '../package-grid-card/package-grid-card.component';
import { CommonModule } from '@angular/common';
import { Package } from '../../shared/types';

/**
 * Displays a responsive grid of package cards and exposes edit, delete, and duplicate actions for each package. This standalone component connects to PackageService to receive a live Observable stream of packages and renders them using PackageGridCardComponent. It acts as a presentational/container hybrid: it accepts an optional packages input array but primarily consumes the service-provided packages$ observable.
 * @component
 * @selector app-package-grid
 * @example
 *   <app-package-grid
 *     [packages]="packageList"
 *     (edit)="onEdit($event)"
 *     (delete)="onDelete($event)"
 *     (duplicate)="onDuplicate($event)">
 *   </app-package-grid>
 * @input any[] packages - Optional static list of packages to render. If not provided, the component uses the internally injected PackageService's packages$ observable.
 * @output EventEmitter<any> edit - Emitted when a package's edit action is triggered. Payload is the package object to edit.
 * @output EventEmitter<any> delete - Emitted when a package's delete action is triggered. Payload is the package object to delete.
 * @output EventEmitter<any> duplicate - Emitted when a package's duplicate action is triggered. Payload is the package object to duplicate.
 *
 * Key methods:
 * - editPackage(pkg: any): Emits the edit event with the given package.
 * - deletePackage(pkg: any): Emits the delete event with the given package.
 * - duplicatePackage(pkg: any): Emits the duplicate event with the given package.
 *
 * Important lifecycle and behavior notes:
 * - constructor injects PackageService and initializes packages$ to the service's packages$ observable for reactive data binding.
 * - No explicit Angular lifecycle hooks (e.g., ngOnInit) are implemented in this class; package data is sourced reactively from packages$.
 * - Designed to be used with PackageGridCardComponent for individual card rendering and CommonModule for Angular common directives.
 */
@Component({
  selector: 'app-package-grid',
  standalone: true,
  imports: [PackageGridCardComponent, CommonModule],
  templateUrl: './package-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './package-grid.component.scss',
})
export class PackageGridComponent {
  @Input() packages: any[] = [];
  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() duplicate = new EventEmitter<any>();
  packages$: Observable<Package[]>;

  constructor(private packageService: PackageService) {
    this.packages$ = this.packageService.packages$;
  }

  editPackage(pkg: any) {
    this.edit.emit(pkg);
  }

  deletePackage(pkg: any) {
    this.delete.emit(pkg);
  }

  duplicatePackage(pkg: any) {
    this.duplicate.emit(pkg);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
